import { Link, useParams } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { useFavorites } from "@/hooks/useFavorites";
import { showToast } from "@/components/feature/Toast";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import VideoPlayer from "@/pages/detail/components/VideoPlayer";
import RelatedList from "@/pages/detail/components/RelatedList";
import {
  formatDate,
  formatPlays,
  getCategoryName,
  getRelatedVideos,
  getVideoById,
} from "@/utils/video";

export default function Detail() {
  const { videoId = "" } = useParams();
  const video = getVideoById(videoId);
  const { toggleFavorite, isFavorite } = useFavorites();
  const fav = video ? isFavorite(video.id) : false;

  useSEO({
    title: video ? `${video.title} - 糖豆视频` : "视频未找到 - 糖豆视频",
    description: video ? video.description : "该视频可能已下线，返回糖豆视频首页发现更多精彩内容。",
    keywords: video
      ? `${video.title},${getCategoryName(video.category)}视频,${video.author},糖豆视频`
      : "糖豆视频",
  });

  if (!video) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <EmptyState
          icon="ri-video-off-line"
          title="没有找到这个视频"
          description="视频可能已被删除或链接有误，去首页看看最新的推荐内容吧。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const related = getRelatedVideos(video, 6);
  const categoryName = getCategoryName(video.category);

  const onToggleFavorite = () => {
    const next = toggleFavorite(video.id);
    showToast(next ? "已加入收藏" : "已取消收藏");
  };

  const onShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("视频链接已复制，快去分享吧");
    } catch {
      showToast("链接已准备好，请手动复制地址栏链接");
    }
  };

  return (
    <div className="w-full px-4 md:px-8 py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: categoryName, to: `/category/${video.category}` },
          { label: video.title },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
        <div className="lg:col-span-2">
          <VideoPlayer video={video} />

          <h1 className="mt-6 text-xl md:text-2xl font-heading font-bold text-foreground-950 leading-snug">
            {video.title}
          </h1>

          <div className="mt-3 flex items-center flex-wrap gap-x-4 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className="ri-user-3-line"></i>
              </span>
              {video.author}
            </span>
            <span>{formatDate(video.publishDate)} 上传</span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-play-circle-line"></i>
              </span>
              {formatPlays(video.plays)} 次播放
            </span>
            <Link
              to={`/category/${video.category}`}
              className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-900 hover:bg-primary-100 hover:text-primary-700 transition-colors"
            >
              {categoryName}
            </Link>
          </div>

          <div className="mt-6 flex items-center flex-wrap gap-3">
            <button
              type="button"
              onClick={onToggleFavorite}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                fav
                  ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                  : "border border-background-300 text-foreground-800 hover:border-accent-400 hover:text-accent-700"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={fav ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
              </span>
              {fav ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={onShare}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 text-sm hover:border-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-share-line"></i>
              </span>
              分享
            </button>
          </div>

          <div className="mt-6 p-5 rounded-lg bg-background-100 border border-background-200">
            <h2 className="text-sm font-heading font-semibold text-foreground-900 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-primary-600">
                <i className="ri-file-text-line"></i>
              </span>
              视频简介
            </h2>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
              {video.description}
            </p>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24">
            <h2 className="flex items-center gap-2 text-lg font-heading font-bold text-foreground-950 mb-5">
              <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
              相关推荐
            </h2>
            <RelatedList videos={related} />
            <Link
              to={`/category/${video.category}`}
              className="mt-8 inline-flex w-full items-center justify-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              查看「{categoryName}」全部视频
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}